import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";

/**
 * BackToTop — small floating circular button that appears after the user
 * scrolls past a threshold and smoothly returns the page to the top.
 *
 * Mounted once at the App root so it overlays every page consistently. It
 * deliberately stays in the bottom-right with a generous safe-area inset so
 * it doesn't sit on top of CTA buttons or footer text.
 *
 * Hidden inside the SmartEWM application shell — that shell uses its own
 * vertical flex layout where the page body never scrolls, so a global
 * scroll-to-top affordance is meaningless there.
 */
export const BackToTop = ({ threshold = 320 }) => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  if (location.pathname.startsWith("/app/smartewmbot")) return null;

  const scrollTop = () => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <button
      type="button"
      onClick={scrollTop}
      aria-label="Back to top"
      data-testid="back-to-top"
      className={`fixed z-40 right-4 bottom-4 sm:right-6 sm:bottom-6 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0093cb] ${
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-2 pointer-events-none"
      }`}
      style={{
        backgroundColor: "#0093cb",
        boxShadow:
          "0 12px 28px -10px rgba(0,147,203,0.55), 0 4px 10px -2px rgba(15,23,42,0.25)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#03738C")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0093cb")}
    >
      <ArrowUp className="w-4.5 h-4.5 sm:w-5 sm:h-5" strokeWidth={2} />
    </button>
  );
};

export default BackToTop;
